import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  DELIVERABLE_TEMPLATE_GET,
  DELIVERABLE_TEMPLATE_LIST,
  createDeliverableTemplate,
  deleteDeliverableTemplate,
  getDeliverableTemplateById,
  getDeliverableTemplates,
  updateDeliverableTemplate,
} from "@/api/client/deliverable-template";
import {
  DeliverableTemplate,
  GetTableListParams,
  UpdateDeliverableTemplatePayload,
} from "@/types";
import { showErrorToast } from "@/lib/errorUtils";

/**
 * A template is a saved prompt and nothing else — no attachments, no branding
 * assets — so these hooks are plain CRUD over `/deliverable-template`.
 */

export const useDeliverableTemplates = (
  params?: Partial<GetTableListParams>,
  options?: { enabled?: boolean },
) =>
  useQuery<DeliverableTemplate[]>({
    queryKey: [DELIVERABLE_TEMPLATE_LIST, params ?? {}],
    queryFn: () => getDeliverableTemplates(params),
    enabled: options?.enabled ?? true,
  });

export const useDeliverableTemplate = (id: string) =>
  useQuery<DeliverableTemplate>({
    queryKey: [DELIVERABLE_TEMPLATE_GET, id],
    queryFn: () => getDeliverableTemplateById(id),
    enabled: !!id,
  });

export const useCreateDeliverableTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDeliverableTemplate,
    onSuccess: (data) => {
      queryClient.setQueryData([DELIVERABLE_TEMPLATE_GET, data.id], data);
      queryClient.invalidateQueries({ queryKey: [DELIVERABLE_TEMPLATE_LIST] });
      toast.success("Template saved.");
    },
    onError: (error) => {
      showErrorToast(error, "deliverables", "Failed to save template.");
    },
  });
};

/**
 * Editing a shared template changes it for everyone immediately;
 * already-generated deliverables are untouched.
 */
export const useUpdateDeliverableTemplate = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateDeliverableTemplatePayload) =>
      updateDeliverableTemplate(id, data),
    onSuccess: (data) => {
      queryClient.setQueryData([DELIVERABLE_TEMPLATE_GET, id], data);
      queryClient.invalidateQueries({ queryKey: [DELIVERABLE_TEMPLATE_LIST] });
      toast.success("Template updated.");
    },
    onError: (error) => {
      showErrorToast(error, "deliverables", "Failed to update template.");
    },
  });
};

export const useDeleteDeliverableTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteDeliverableTemplate(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: [DELIVERABLE_TEMPLATE_GET, id] });
      queryClient.invalidateQueries({ queryKey: [DELIVERABLE_TEMPLATE_LIST] });
      toast.success("Template deleted.");
    },
    onError: (error) => {
      showErrorToast(error, "deliverables", "Failed to delete template.");
    },
  });
};
